// src/pages/Transactions.js
import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Chip,
  Paper,
} from '@mui/material';

// Helper function to style each transaction item based on direction
const getTransactionStyle = (direction) => {
  if (direction === 'Sent') {
    // Light green background with a green left border
    return {
      backgroundColor: '#e8f5e9',
      borderLeft: '5px solid #66bb6a',
      marginBottom: '8px',
      borderRadius: '4px',
    };
  } else if (direction === 'Received') {
    // Light blue background with a blue left border
    return {
      backgroundColor: '#e3f2fd',
      borderLeft: '5px solid #42a5f5',
      marginBottom: '8px',
      borderRadius: '4px',
    };
  }
  // Default style if neither
  return {
    marginBottom: '8px',
    borderRadius: '4px',
  };
};

// Safely parse the JSON in tx.details
const parseDetails = (detailsStr) => {
  if (!detailsStr) return {};
  try {
    return JSON.parse(detailsStr);
  } catch (err) {
    console.warn("Failed to parse transaction details:", err);
    return {};
  }
};

function Transactions() {
  const [piUser, setPiUser] = useState(null); // Will store Pi user info from Pi SDK
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // 1. Authenticate with Pi SDK on mount
  useEffect(() => {
    if (!window.Pi) {
      setErrorMsg("Pi SDK not loaded.");
      return;
    }
    window.Pi.authenticate(["payments", "username"]) // or the scopes you need
      .then((authResult) => {
        // authResult.user => { uid, username }
        setPiUser(authResult.user);
      })
      .catch((err) => {
        console.error("Pi authentication failed:", err);
        setErrorMsg("Pi authentication failed: " + err.message);
      });
  }, []);

  // 2. Fetch transactions once we have piUser
  useEffect(() => {
    if (!piUser?.uid) return; // Wait until user is authenticated

    setLoading(true);
    const uid = piUser.uid; // Pi user ID
    const url = `https://lemda.co.uk/api/fetch_payments.php?uid=${encodeURIComponent(uid)}`;

    fetch(url)
      .then((resp) => resp.json())
      .then((data) => {
        if (data.success) {
          setTransactions(data.payments);
        } else {
          console.error("Error fetching transactions:", data.message);
          setErrorMsg(data.message);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
        setErrorMsg("Fetch error: " + error.message);
        setLoading(false);
      });
  }, [piUser]);

  return (
    <Box p={2}>
      <Typography variant="h4" gutterBottom>
        Your Transactions
      </Typography>

      {errorMsg && (
        <Typography color="error" gutterBottom>
          {errorMsg}
        </Typography>
      )}

      {loading ? (
        <CircularProgress />
      ) : transactions.length === 0 ? (
        <Typography>No transactions found.</Typography>
      ) : (
        <List>
          {transactions.map((tx) => {
            // Determine if this transaction is Sent or Received
            let direction = "Unknown";
            if (tx.user_id === piUser?.uid) {
              direction = "Sent";
            } else if (tx.receiver_id === piUser?.uid) {
              direction = "Received";
            }

            // Parse details JSON: { amount, commission, totalCharge, etc. }
            const detailObj = parseDetails(tx.details);

            // We'll show the status as a small Chip
            const statusLabel = tx.status
              ? tx.status.charAt(0).toUpperCase() + tx.status.slice(1)
              : "Unknown";

            return (
              <Paper key={tx.id} sx={getTransactionStyle(direction)}>
                <ListItem>
                  <ListItemText
                    primary={
                      <>
                        <Typography variant="subtitle1" component="div">
                          <strong>{direction} Transaction</strong>{" "}
                          <Chip
                            label={statusLabel}
                            color={tx.status === 'completed' ? 'success' : 'warning'}
                            size="small"
                            sx={{ ml: 1, verticalAlign: 'middle' }}
                          />
                        </Typography>
                      </>
                    }
                    secondary={
                      <>
                        {/* Payment ID & Date */}
                        <Typography variant="body2">
                          <strong>Payment ID:</strong> {tx.payment_id}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Date:</strong> {tx.created_at}
                        </Typography>
                        {/* Amount Details from "details" JSON */}
                        {detailObj.amount && (
                          <Typography variant="body2">
                            <strong>Amount:</strong> {detailObj.amount} Pi
                          </Typography>
                        )}
                        {detailObj.commission && (
                          <Typography variant="body2">
                            <strong>Fee (2%):</strong> {detailObj.commission} Pi
                          </Typography>
                        )}
                        {detailObj.totalCharge && (
                          <Typography variant="body2">
                            <strong>Total Charge:</strong> {detailObj.totalCharge} Pi
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              </Paper>
            );
          })}
        </List>
      )}
    </Box>
  );
}

export default Transactions;
